'use client';

import type { ActiveSubscription } from '@ps/models';

import {
  longCommitmentTotal,
  longerCommitment,
} from '../../subscription/configuration/functions/pricing';

interface Props {
  subscription: ActiveSubscription;
  selected: boolean;
  onSelect?: (value: boolean) => void;
}

/**
 * Term toggle shown only for VIP tiers that offer a longer-commitment discount:
 * monthly vs the discounted multi-month term (with an `N% Off` badge). Ports
 * PopshopRN's `LongerCommitmentDiscountSelect`; the discount math comes from the
 * shared `longerCommitment` / `longCommitmentTotal` helpers.
 */
export function LongerCommitmentDiscountSelect({
  subscription,
  selected,
  onSelect,
}: Props) {
  const price = subscription.subscription_data.creator_subscription_price;
  const term = longerCommitment(price);
  if (!term) return null;

  const currency = price.creator_sub_price_currency || 'USD';
  const monthly = price.creator_sub_price_without_precision / 100;
  const monthlyLabel = `${currency} ${monthly.toFixed(2)}/mo.`;
  const longerTotal = longCommitmentTotal(monthly, term);
  const longerLabel = `${currency} ${longerTotal.toFixed(2)}/${term.months} mo.`;

  return (
    <div className="flex flex-col gap-2">
      <hr className="border-line-strong" />
      <p className="text-content-3 text-xs font-light">Select Preferred Term</p>
      <div className="flex flex-wrap items-stretch gap-2">
        <TermOption
          label={monthlyLabel}
          selected={!selected}
          onSelect={() => onSelect?.(false)}
        />
        <TermOption
          label={longerLabel}
          badge={`${term.discountPercentage}% Off`}
          selected={selected}
          onSelect={() => onSelect?.(true)}
        />
      </div>
    </div>
  );
}

function TermOption({
  label,
  selected,
  badge,
  onSelect,
}: {
  label: string;
  selected: boolean;
  badge?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={event => {
        // Don't let the toggle bubble up to the cell's select handler.
        event.stopPropagation();
        onSelect();
      }}
      className="border-line-strong bg-surface-2 relative flex flex-1 items-center justify-center gap-2 rounded border p-2"
    >
      <span
        className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border ${
          selected ? 'border-brand bg-brand' : 'border-line-strong'
        }`}
      >
        {selected && <span className="bg-surface-2 size-1.5 rounded-full" />}
      </span>
      <span className="text-content-1 text-xs leading-[1.2]">{label}</span>
      {badge && (
        <span className="bg-brand text-content-on-brand absolute right-1 top-0 -translate-y-1/2 rounded-full px-1.5 py-1 text-[8px] font-medium leading-none">
          {badge}
        </span>
      )}
    </button>
  );
}
